import { createApiRef } from "@backstage/core-plugin-api";
import type { DiscoveryApi } from "@backstage/core-plugin-api";
import axios from "axios";
import axiosRetry from "axios-retry";
import pLimit from "p-limit";

export interface ChatMessage {
	role: "user" | "assistant";
	content: string;
}

export interface ChatResponse {
	response: string;
	rawResponse: string;
	references: string[];
	progress?: number;
}

export interface ChatRequestStatus {
	isLoading: boolean;
	progress: number;
	error: string | null;
}

export const chatbotApiRef = createApiRef<ChatbotApi>({
	id: "plugin.chatbot",
});

export interface ChatbotApi {
	sendMessage(
		message: string,
		conversationHistory: ChatMessage[],
		fileContexts?: string[],
		onStatusUpdate?: (status: ChatRequestStatus) => void,
	): Promise<string>;
	cancelRequest(): void;
}
export interface FileContext {
	repo: string;
	file: string;
}

export interface ChatMessage {
	role: "user" | "assistant";
	content: string;
}
export class ChatbotApiClient implements ChatbotApi {
	private readonly discoveryApi: DiscoveryApi;
	private readonly concurrencyLimit: number;
	private readonly limit: ReturnType<typeof pLimit>;
	private currentController: AbortController | null = null;

	constructor(options: {
		discoveryApi: DiscoveryApi;
		maxConcurrentRequests?: number;
		defaultTimeout?: number;
	}) {
		this.discoveryApi = options.discoveryApi;
		this.concurrencyLimit = options.maxConcurrentRequests || 50;
		this.limit = pLimit(this.concurrencyLimit);

		axiosRetry(axios, {
			retries: 3,
			retryDelay: (retryCount) => {
				return axiosRetry.exponentialDelay(retryCount) + Math.random() * 1000;
			},
			retryCondition: (error) => {
				return (
					axiosRetry.isNetworkOrIdempotentRequestError(error) ||
					error.code === "ECONNABORTED" ||
					(error.response?.status ? error.response.status >= 500 : false)
				);
			},
			onRetry: (retryCount, _error, requestConfig) => {
				console.log(
					`Retry attempt ${retryCount} for request:`,
					requestConfig.url,
				);
			},
		});
	}

	cancelRequest() {
		if (this.currentController) {
			this.currentController.abort();
			this.currentController = null;
		}
	}

	async sendMessage(
		message: string,
		conversationHistory: ChatMessage[],
		fileContexts?: string[],
		onStatusUpdate?: (status: ChatRequestStatus) => void,
	): Promise<string> {
		if (fileContexts?.length) {
			// Log the incoming file contexts for debugging
			console.log("Raw file contexts:", fileContexts);
		}

		const baseUrl = await this.discoveryApi.getBaseUrl("chatbot");
		this.currentController = new AbortController();

		const updateStatus = (status: Partial<ChatRequestStatus>) => {
			onStatusUpdate?.({
				isLoading: false,
				progress: 0,
				error: null,
				...status,
			});
		};

		return this.limit(async () => {
			try {
				updateStatus({ isLoading: true, progress: 0 });

				const response = await axios.post<ChatResponse>(
					`${baseUrl}/chat`,
					{
						message,
						conversationHistory,
						fileContexts: fileContexts || [],
					},
					{
						headers: {
							"Content-Type": "application/json",
							"X-Request-ID": `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
						},
						signal: this.currentController?.signal,
						timeout: 180000,
					},
				);

				updateStatus({ isLoading: false, progress: 100 });

				return response.data.response;
			} catch (error) {
				if (axios.isAxiosError(error)) {
					console.error("Axios error:", error.message);
					if (error.code === "ECONNABORTED") {
						return "Request timed out. Please try again.";
					}
					return `Error communicating with chat service: ${error.message}`;
				}
				console.error("Unknown error:", error);
				return "Error communicating with chat service: Unknown error";
			} finally {
				this.currentController = null;
			}
		});
	}
}
